/**
 * Sandbox Authentication Module
 *
 * Detects sandbox environment and bypasses Supabase auth for testing
 * ONLY active when REACT_APP_API_URL contains 'sandbox'
 * Safe to merge - automatically disabled in production
 */

const API_URL = process.env.REACT_APP_API_URL || '';
const IS_SANDBOX = API_URL.includes('sandbox');

/**
 * Check if we're in sandbox mode
 */
export const isSandboxMode = () => IS_SANDBOX;

/**
 * Sandbox authentication bypass
 * Returns mock session for testing without Supabase
 */
export const sandboxAuthBypass = async () => {
  if (!IS_SANDBOX) {
    throw new Error('Sandbox auth only available in sandbox environment');
  }

  try {
    const response = await fetch(`${API_URL}/sandbox/auth/bypass`);

    if (!response.ok) {
      throw new Error(`Sandbox auth failed: ${response.status}`);
    }

    const data = await response.json();

    // Store mock session in localStorage (mimics Supabase)
    localStorage.setItem('sb-sandbox-auth-token', JSON.stringify(data.session));
    localStorage.setItem('sb-sandbox-user', JSON.stringify(data.user));

    return data;
  } catch (error) {
    console.error('Sandbox auth bypass failed:', error);
    throw error;
  }
};

/**
 * Get sandbox user profile
 * Returns mock profile for testing
 */
export const getSandboxUserProfile = async () => {
  if (!IS_SANDBOX) {
    throw new Error('Sandbox profile only available in sandbox environment');
  }

  try {
    const response = await fetch(`${API_URL}/sandbox/user/profile`);

    if (!response.ok) {
      throw new Error(`Sandbox profile failed: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Sandbox user profile failed:', error);
    throw error;
  }
};

/**
 * Get current sandbox session from localStorage
 */
export const getSandboxSession = () => {
  if (!IS_SANDBOX) return null;

  try {
    const sessionStr = localStorage.getItem('sb-sandbox-auth-token');
    const userStr = localStorage.getItem('sb-sandbox-user');

    if (!sessionStr || !userStr) return null;

    return {
      session: JSON.parse(sessionStr),
      user: JSON.parse(userStr)
    };
  } catch (error) {
    console.error('Failed to get sandbox session:', error);
    return null;
  }
};

/**
 * Clear sandbox session
 */
export const clearSandboxSession = () => {
  localStorage.removeItem('sb-sandbox-auth-token');
  localStorage.removeItem('sb-sandbox-user');
};

/**
 * Sandbox-aware sign out
 */
export const sandboxSignOut = () => {
  if (IS_SANDBOX) {
    clearSandboxSession();
  }
};
